import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form, Input, Select, Tooltip, DatePicker } from 'antd';
import dayjs from 'dayjs';
import s from './s.module.less';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { IBiographicForm, IBackgroundInfo } from 'types/newUniprofile';
import { EMPTY_PLACEHOLDER } from 'constants/common';
import { isEqual } from 'lodash';

interface Props {
    initValue?: IBiographicForm;
    isEditing?: boolean;
    hasContent?: boolean;
    updateFn: (data: Partial<IBackgroundInfo>) => Promise<void>;
}

const Education = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, hasContent, updateFn } = props;
    const [form] = Form.useForm();
    const [setting] = useSettings();
    const ethnicityOptions = setting[EnumFields.ETHNICITY] || [];
    const religionOptions = setting[EnumFields.RELIGION] || [];
    const prevInitValueRef = useRef<IBiographicForm>();

    useEffect(() => {
        if (initValue && !isEqual(prevInitValueRef.current, initValue)) {
            form.setFieldsValue({
                ...initValue,
                birthDate: initValue.birthDate ? dayjs(initValue.birthDate) : null,
            });
            prevInitValueRef.current = initValue;
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            await updateFn({
                biographic: values,
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
    }));

    const renderEditing = () => {
        return (
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                <div className="flex gap-4">
                    <Form.Item
                        label="Ethnicity"
                        name="ethnicity"
                        className="flex-1"
                    >
                        <Select options={ethnicityOptions} allowClear />
                    </Form.Item>
                    <Form.Item
                        label={
                            <div className={s.formLabel}>Date of birth
                                <Tooltip color="#FFFFFF" title={<div className={s.tipText}>Your birth date will only be used to calculate your age. Your specific birth date will not be used in any public profile.</div>}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                        }
                        name="birthDate"
                        className="flex-1"
                    >
                        <DatePicker defaultPickerValue={dayjs('1980-01-01')} style={{ width: '100%' }} showToday={false} format="YYYY-MM-DD" />
                    </Form.Item>
                </div>

                <div className="flex gap-4">
                    <Form.Item
                        label="Religion"
                        name="religion"
                        className="flex-1"
                    >
                        <Select options={religionOptions} allowClear />
                    </Form.Item>
                    <Form.Item
                        label={
                            <div className={s.formLabel}>Used name
                                <Tooltip color="#FFFFFF" title={<div className={s.tipText}>This information helps us match your existing profile, if it’s under a different name.</div>}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                        }
                        name="usedName"
                        className="flex-1"
                    >
                        <Input />
                    </Form.Item>
                </div>
            </Form>
        );
    };

    const renderReview = () => {
        if (!hasContent || !initValue) {
            return (
                <div>
                    Not provided
                </div>
            );
        }

        const { ethnicity, birthDate, religion, usedName } = initValue;
        return (
            <div>
                <div className={s.row}>
                    <div
                        className={s.displayItem}
                    >
                        <div className={s.label}>Ethnicity</div>
                        <div className={s.value}>{ethnicity || EMPTY_PLACEHOLDER}</div>
                    </div>
                </div>
                <div className={s.row}>
                    <div
                        className={s.displayItem}
                    >
                        <div className={s.label}>Birth date</div>
                        <div className={s.value}>{birthDate ? dayjs(birthDate).format('YYYY-MM-DD') : EMPTY_PLACEHOLDER}</div>
                    </div>
                </div>
                <div className={s.row}>
                    <div
                        className={s.displayItem}
                    >
                        <div className={s.label}>Religion</div>
                        <div className={s.value}>{religion || EMPTY_PLACEHOLDER}</div>
                    </div>
                </div>
                <div className={s.row}>
                    <div
                        className={s.displayItem}
                    >
                        <div className={s.label}>Used name</div>
                        <div className={s.value}>{usedName || EMPTY_PLACEHOLDER}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mt-2">
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default Education;
